/**
 * Action renderer for the Storyblok Visual Editor preview.
 *
 * Mirrors the consumer benefits-mobile-app action detail card design:
 *   - Bold heading
 *   - "Earn $X" reward badge below (templated; we substitute ${amount} with
 *     a {AMOUNT} placeholder since the live reward isn't available here)
 *   - Optional hero image
 *   - Description (Markdown)
 *   - Primary CTA button (filled, centered, rounded pill)
 *   - Optional secondary CTA rendered as a text link below the primary
 *
 * Three entrypoints (matching the FaqPreview structure):
 *   - <ActionPagePreview>     : story.component === 'content_page'     (full page; iterates categories → items)
 *   - <ActionCategoryPreview> : story.component === 'content_category' (single category section)
 *   - <ActionItemPreview>     : story.component === 'action_item'      (single card)
 *
 * Click-to-edit: every block is wrapped with `storyblokEditable()` props
 * so clicking inside the iframe focuses the matching field in Storyblok.
 */

import React from 'react';
import { Box, Stack, Typography, Alert, Link as MuiLink } from '@mui/material';
import { storyblokEditable } from '@storyblok/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ── Theme tokens — mirror benefits-mobile-app ActionDetails (desktop web) ──
// task_desc/heading=#0D1C3D, taskType(reward)=#677083, screen bg=GRAY_100,
// CTA = brand button1Color (default blue), pill on desktop (borderRadius 80).
const COLORS = {
  textColor: '#0D1C3D',      // task_desc + heading (real uses tenant headerBgColor)
  mutedText: '#4A546A',
  cardBg: '#FFFFFF',         // desktop contentBgColor
  pageBg: '#F4F5F6',         // screenBgColor (GRAY_100)
  primaryBg: '#3A7BC8',      // button1Color fallback
  primaryFg: '#FFFFFF',
  secondaryFg: '#0D1C3D',
  rewardLabelFg: '#677083',  // styles.taskType
};

const CARD_SHADOW = '0px 12px 32px 0px rgba(233, 235, 238, 0.55)';
const REWARD_AMOUNT_PLACEHOLDER = '{AMOUNT}';

// ── Types — mirror the CDN response with resolve_relations applied ────
interface StoryblokRel {
  _uid?: string;
  _editable?: string;
}

export interface ActionItemBlok extends StoryblokRel {
  component: 'action_item';
  heading?: string;
  description?: string;
  secondary_button_text?: string;
  secondary_button_url?: { url?: string; cached_url?: string } | string;
  image?: { filename?: string; alt?: string } | null;
}

interface ResolvedItem {
  uuid?: string;
  content?: ActionItemBlok;
}

interface ActionCategoryBlok extends StoryblokRel {
  component: 'content_category';
  // `name` is the content_category schema's display field (what create/rename
  // write and editors change); `category_name` is a legacy out-of-schema key.
  name?: string;
  category_name?: string;
  items?: ResolvedItem[] | string[];
}

interface ResolvedCategory {
  uuid?: string;
  name?: string;
  content?: ActionCategoryBlok;
}

interface ActionPageBlok extends StoryblokRel {
  component: 'content_page';
  categories?: ResolvedCategory[] | string[];
}

// ── Helpers ───────────────────────────────────────────────────────────
function isResolved<T extends object>(x: unknown): x is T {
  return typeof x === 'object' && x !== null;
}

function editableProps(blok: StoryblokRel | undefined): Record<string, string> {
  if (!blok) return {};
  return storyblokEditable(blok as unknown as Parameters<typeof storyblokEditable>[0]) as Record<string, string>;
}

function linkHref(field: ActionItemBlok['secondary_button_url']): string | undefined {
  if (!field) return undefined;
  if (typeof field === 'string') return field || undefined;
  return field.url || field.cached_url || undefined;
}

/**
 * Inline Markdown renderer — strips outer `<p>` so the content flows in
 * place of a single piece of text (heading row, button label, etc.).
 * Schema marks heading / reward_label / button1_text / secondary_button_text as
 * Markdown, so we honour that here while keeping the layout single-line.
 */
function MarkdownInline({ text }: { text?: string | null }) {
  if (!text) return null;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Flatten the wrapping <p> so the content is inline-flowable.
        p: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
        // Disallow block-level promotion inside short fields — render them
        // as spans so they don't introduce extra vertical rhythm.
        h1: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
        h2: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
        h3: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
        h4: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
        h5: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
        h6: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

// ── Single action card ────────────────────────────────────────────────
interface ActionItemCardProps {
  blok: ActionItemBlok;
  rewardAmount?: string | null;
}

function ActionItemCard({ blok, rewardAmount }: ActionItemCardProps) {
  const button2Href = linkHref(blok.secondary_button_url);
  const imageUrl = blok.image?.filename || null;

  // Mirrors ActionDetails (desktop web): a 480px white rounded-16 container,
  // padding 24, left-aligned, with the content stack (icon → title+reward →
  // description) separated by a 32px gap, and a full-width pill CTA footer.
  return (
    <Box
      {...editableProps(blok)}
      sx={{
        background: COLORS.cardBg,
        borderRadius: '16px',
        boxShadow: CARD_SHADOW,
        p: 3,                 // 24px — styles.container padding
        maxWidth: 480,        // desktop container maxWidth
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* contentContainer — gap 32 between icon / header / description */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Task icon — 60×60 inside an 80 box, left-aligned (desktop has no
            circular background; the icon sits flush on the card). */}
        {imageUrl && (
          <Box
            sx={{
              width: 80,
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt={blok.image?.alt || ''}
              sx={{ width: 48, height: 48, objectFit: 'contain' }}
            />
          </Box>
        )}

        {/* Header group — title + reward, 8px gap (styles task header View) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {blok.heading && (
            <Typography
              component="h1"
              sx={{
                color: COLORS.textColor,   // real: tenantUxthemeColors.headerBgColor
                fontWeight: 800,
                fontSize: 32,              // task_title (desktop)
                lineHeight: '38.4px',
              }}
            >
              <MarkdownInline text={blok.heading} />
            </Typography>
          )}

          <Typography
            sx={{
              color: COLORS.rewardLabelFg,  // styles.taskType
              fontSize: 16,
              fontWeight: 500,
              lineHeight: '22.4px',
            }}
          >
            Earn {rewardAmount ?? REWARD_AMOUNT_PLACEHOLDER}
          </Typography>
          {!rewardAmount && (
            <Typography sx={{ fontSize: 11, color: COLORS.mutedText, mt: 0.5, fontStyle: 'italic' }}>
              * Exact amount will display in the platform UI
            </Typography>
          )}
        </Box>

        {blok.description && (
          <Box
            sx={{
              color: COLORS.textColor,    // task_desc
              fontSize: 16,
              lineHeight: 1.4,            // 22.4 / 16
              '& p:first-of-type': { mt: 0 },
              '& p:last-of-type': { mb: 0 },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{blok.description}</ReactMarkdown>
          </Box>
        )}
      </Box>

      {/* buttonContainer — full-width pill CTA (real: borderRadius 80 on desktop,
          backgroundColor button1Color). Label is hard-wired in the app. */}
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            backgroundColor: COLORS.primaryBg,
            color: COLORS.primaryFg,
            fontWeight: 600,
            fontSize: 16,
            py: 1.75,                  // ≈ 51px button height
            px: 8,
            borderRadius: '80px',      // pill (desktop)
            width: '100%',
            textAlign: 'center',
          }}
        >
          Get started
        </Box>

        {blok.secondary_button_text && (
          <Box sx={{ textAlign: 'center', pt: 0.5 }}>
            <MuiLink
              href={button2Href}
              underline="none"
              sx={{
                color: COLORS.secondaryFg,
                fontWeight: 600,
                fontSize: 15,
                cursor: button2Href ? 'pointer' : 'default',
                '&:hover': { textDecoration: button2Href ? 'underline' : 'none' },
              }}
            >
              <MarkdownInline text={blok.secondary_button_text} />
            </MuiLink>
          </Box>
        )}
      </Box>

      {!blok.heading && !blok.description && (
        <Typography sx={{ color: COLORS.mutedText, fontStyle: 'italic', textAlign: 'center', mt: 2 }}>
          Empty action card — fill in heading / description on the right.
        </Typography>
      )}
    </Box>
  );
}

// ── Item-level preview (entry: action_item story) ─────────────────────
interface ActionItemPreviewProps {
  itemBlok: ActionItemBlok;
  rewardAmount?: string | null;
}

export function ActionItemPreview({ itemBlok, rewardAmount }: ActionItemPreviewProps) {
  return (
    <Box sx={{ background: COLORS.pageBg, py: { xs: 3, md: 5 }, mx: -4 }}>
      <ActionItemCard blok={itemBlok} rewardAmount={rewardAmount} />
    </Box>
  );
}

// ── Category-level preview (entry: content_category under action) ─────
interface ActionCategoryPreviewProps {
  categoryBlok: ActionCategoryBlok;
  rewardAmount?: string | null;
}

export function ActionCategoryPreview({ categoryBlok, rewardAmount }: ActionCategoryPreviewProps) {
  const itemsField = (categoryBlok.items ?? []) as Array<ResolvedItem | string>;
  const items: ActionItemBlok[] = itemsField
    .filter((it): it is ResolvedItem => isResolved<ResolvedItem>(it))
    .map((it) => it.content)
    .filter((c): c is ActionItemBlok => !!c);

  return (
    <Box {...editableProps(categoryBlok)} sx={{ background: COLORS.pageBg, py: { xs: 3, md: 5 }, mx: -4 }}>
      {(() => {
        const catName = categoryBlok.name || categoryBlok.category_name;
        return catName && catName !== 'Default' ? (
          <Typography
            component="h2"
            sx={{ color: COLORS.textColor, fontWeight: 700, fontSize: 18, mb: 3, textAlign: 'center' }}
          >
            {catName}
          </Typography>
        ) : null;
      })()}
      {items.length === 0 ? (
        <Alert severity="info" sx={{ maxWidth: 540, mx: 'auto' }}>
          No action variants in this category yet. Add an <code>action_item</code> story
          inside the category folder in Storyblok.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {items.map((it, i) => (
            <ActionItemCard key={it._uid || i} blok={it} rewardAmount={rewardAmount} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

// ── Page-level preview (entry: content_page under action) ─────────────
interface ActionPagePreviewProps {
  pageBlok: ActionPageBlok;
  rewardAmount?: string | null;
}

export function ActionPagePreview({ pageBlok, rewardAmount }: ActionPagePreviewProps) {
  const categories = (pageBlok.categories ?? []) as Array<ResolvedCategory | string>;
  const resolved = categories.filter(
    (c): c is ResolvedCategory => isResolved<ResolvedCategory>(c),
  );
  const unresolvedCount = categories.length - resolved.length;

  // Flatten across categories — actions almost always live in a single
  // "Default" category, so the page preview renders all variants stacked.
  const flatItems: ActionItemBlok[] = resolved.flatMap((cat) => {
    const itemsField = (cat.content?.items ?? []) as Array<ResolvedItem | string>;
    return itemsField
      .filter((it): it is ResolvedItem => isResolved<ResolvedItem>(it))
      .map((it) => it.content)
      .filter((c): c is ActionItemBlok => !!c);
  });

  return (
    <Box {...editableProps(pageBlok)} sx={{ background: COLORS.pageBg, py: { xs: 3, md: 5 }, mx: -4 }}>
      {resolved.length === 0 && unresolvedCount === 0 && (
        <Alert severity="info" sx={{ maxWidth: 540, mx: 'auto' }}>
          This action page has no categories yet. Add a <code>content_category</code> story
          inside the page folder.
        </Alert>
      )}

      {unresolvedCount > 0 && (
        <Alert severity="warning" sx={{ maxWidth: 540, mx: 'auto', mb: 3 }}>
          {unresolvedCount} categor{unresolvedCount === 1 ? 'y was' : 'ies were'} returned
          as a UUID but not resolved — check the fetch params.
        </Alert>
      )}

      {flatItems.length === 0 && resolved.length > 0 ? (
        <Alert severity="info" sx={{ maxWidth: 540, mx: 'auto' }}>
          No action variants yet. Add an <code>action_item</code> story under the page's category folder.
        </Alert>
      ) : (
        <Stack spacing={3}>
          {flatItems.map((it, i) => (
            <ActionItemCard key={it._uid || i} blok={it} rewardAmount={rewardAmount} />
          ))}
        </Stack>
      )}
    </Box>
  );
}
